import { z } from 'zod';

/**
 * Vocabulario do protocolo de avaliacao morfofuncional.
 *
 * REGRA ARQUITETURAL: este arquivo nao conhece React, Dexie nem Supabase.
 * A especificacao e dado puro; o engine de validacao sao funcoes puras.
 * Ver docs/decisoes/0001-spec-executavel.md
 */

// ---------------------------------------------------------------------------
// Vocabulario basico
// ---------------------------------------------------------------------------

/** Nivel de protocolo. 1 = censo, 2 = completo, 3 = completo + dinamica. */
export const ProtocolLevel = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type ProtocolLevel = z.infer<typeof ProtocolLevel>;

/** Instrumento de medicao. Corresponde a coluna "Instr." da ficha. */
export const Instrument = z.enum(['hipometro', 'compasso', 'fita', 'goniometro', 'balanca']);
export type Instrument = z.infer<typeof Instrument>;

/** Como a observacao foi obtida. Na Fase 2 entra 'sensor'. */
export const Method = z.enum(['campo', 'foto', 'sensor']);
export type Method = z.infer<typeof Method>;

/** Lado do animal. A ficha v1.0 mede sempre o lado ESQUERDO; aprumos sao por lado. */
export const Side = z.enum(['E', 'D', 'NA']);
export type Side = z.infer<typeof Side>;

export const Sex = z.enum(['macho_inteiro', 'castrado', 'femea']);
export type Sex = z.infer<typeof Sex>;

/**
 * `erro` bloqueia o fechamento da ficha.
 * `aviso` exige justificativa digitada, gravada em validation_override.
 * Nenhum dos dois e silenciosamente ignoravel.
 */
export const Severity = z.enum(['erro', 'aviso']);
export type Severity = z.infer<typeof Severity>;

export const TraitKind = z.enum(['linear', 'perimetro', 'angulo', 'aprumo', 'escore']);
export type TraitKind = z.infer<typeof TraitKind>;

/** Agrupamento visual no formulario e na ficha impressa. */
export const TraitGroup = z.enum([
  'altura',
  'comprimento',
  'largura',
  'perimetro',
  'angulo',
  'aprumo_frente',
  'aprumo_perfil_anterior',
  'aprumo_perfil_posterior',
  'aprumo_tras',
]);
export type TraitGroup = z.infer<typeof TraitGroup>;

// ---------------------------------------------------------------------------
// Trait
// ---------------------------------------------------------------------------

/**
 * Um trait e uma variavel observavel. O `code` e IMUTAVEL e atravessa versoes
 * do protocolo: a tabela `measurement` referencia o codigo canonico, nunca a
 * versao. Reusar um codigo com significado diferente corrompe o banco de forma
 * irreversivel.
 */
export const Trait = z.object({
  code: z.string().regex(/^[A-Z][A-Z0-9_]{2,15}$/),
  kind: TraitKind,
  group: TraitGroup,
  label: z.string().min(1),
  /** Definicao operacional — o texto do Guia Rapido (folhas 7-8 da ficha). */
  definition: z.string().min(1),
  unit: z.enum(['cm', 'graus', 'kg', 'escore']),
  decimals: z.int().min(0).max(2),
  instrument: Instrument.optional(),
  /** Aceita valor por lado (E/D). Falso = uma unica observacao com side 'NA'. */
  sided: z.boolean().default(false),
  /** Nivel minimo de protocolo em que o trait e coletado. */
  levelMin: ProtocolLevel,
  /** Faixa biologicamente plausivel. PROVISORIA — ver nota no topo de traits.v1.ts. */
  minPlausible: z.number(),
  maxPlausible: z.number(),
  /** Pontos anatomicos (M1..M18) que definem um angulo. */
  points: z.array(z.string()).optional(),
  sortOrder: z.int(),
});
export type Trait = z.infer<typeof Trait>;

export type TraitCode = string;

// ---------------------------------------------------------------------------
// Regras de validacao — vocabulario FECHADO, sem linguagem de expressao
// ---------------------------------------------------------------------------

/**
 * `id` e a chave estavel citada por validation_override.rule_id. Regras sintetizadas
 * a partir do catalogo usam o codigo do trait (RNG:A_CER), nunca um indice — um
 * indice mudaria de significado ao inserir um trait novo e corromperia o historico
 * de justificativas.
 */
const RuleBase = {
  id: z.string().regex(/^[A-Z]{3}[-:][A-Z0-9_]{1,24}$/),
  message: z.string().min(1),
};

/** Valor fora da faixa plausivel, opcionalmente restrita por sexo/idade. */
export const RangeRule = z.object({
  ...RuleBase,
  kind: z.literal('range'),
  trait: z.string(),
  min: z.number(),
  max: z.number(),
  scope: z
    .object({ sex: z.array(Sex).optional(), ageMonths: z.tuple([z.number(), z.number()]).optional() })
    .optional(),
  severity: Severity,
});

/** Campo obrigatorio a partir de um nivel de protocolo. */
export const RequiredRule = z.object({
  ...RuleBase,
  kind: z.literal('required'),
  trait: z.string(),
  when: z.object({
    levelAtLeast: ProtocolLevel,
    sexIn: z.array(Sex).optional(),
    fitnessApto: z.boolean().optional(),
  }),
  severity: Severity,
});

/**
 * Identidade linear entre medidas. A consistencia geometrica da folha 3
 * (P_TOR + A_SUB ~= A_CER, tolerancia +/- 2 cm) e um caso desta regra —
 * dado declarativo, nao um `if` perdido no codigo.
 */
export const LinearIdentityRule = z.object({
  ...RuleBase,
  kind: z.literal('linearIdentity'),
  terms: z.array(z.object({ trait: z.string(), coef: z.number() })).min(1),
  equals: z.string(),
  tolerance: z.number().positive(),
  unit: z.literal('cm'),
  severity: Severity,
});

/** Proporcao entre duas medidas dentro de uma faixa esperada. */
export const RatioRule = z.object({
  ...RuleBase,
  kind: z.literal('ratio'),
  num: z.string(),
  den: z.string(),
  min: z.number(),
  max: z.number(),
  severity: Severity,
});

/**
 * Valvula de escape para a meia duzia de regras irregulares. O NOME vai no
 * spec (dado puro, serializavel, sem eval); a implementacao e uma funcao TS
 * registrada no mapa de predicados.
 */
export const PredicateName = z.enum([
  'prenhezExigeMeses',
  'claudicacaoBloqueiaDinamica',
  'chegadaRecenteForcaNivel1',
  'ferrageamentoNaoPodeSerFuturo',
  'peAbdExigeHora',
  'duplaMedicaoExigeSegundoAvaliador',
  'pesoEEccObrigatorios',
]);
export type PredicateName = z.infer<typeof PredicateName>;

export const PredicateRule = z.object({
  ...RuleBase,
  kind: z.literal('predicate'),
  name: PredicateName,
  severity: Severity,
});

export const Rule = z.discriminatedUnion('kind', [
  RangeRule,
  RequiredRule,
  LinearIdentityRule,
  RatioRule,
  PredicateRule,
]);
export type Rule = z.infer<typeof Rule>;

// ---------------------------------------------------------------------------
// Marcadores anatomicos
// ---------------------------------------------------------------------------

export const Marker = z.object({
  code: z.string().regex(/^M\d{1,2}$/),
  label: z.string().min(1),
  sortOrder: z.int(),
});
export type Marker = z.infer<typeof Marker>;

// ---------------------------------------------------------------------------
// Especificacao completa
// ---------------------------------------------------------------------------

export const ProtocolSpec = z.object({
  code: z.string().regex(/^v\d+\.\d+$/),
  publishedAt: z.iso.date().nullable(),
  status: z.enum(['rascunho', 'publicado', 'aposentado']),
  traits: z.array(Trait).min(1),
  rules: z.array(Rule),
  markers: z.array(Marker),
});
export type ProtocolSpec = z.infer<typeof ProtocolSpec>;
